import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { userService } from "@/services/userService";
import API from "@/services/api";
import { EXECUTION_STATUS_FLOW, STATUS_VARIANT_MAP } from "@/constants/globalConstants";
// import { getProjectPipelineDisplayStatus } from "@/pages/sd-projects-flow-final/constants/projectPipelineStages";
const getProjectPipelineDisplayStatus = (p) => p?.status || "";

/**
 * Hook to derive and format customer statistics from raw backend stats.
 * @param {Object} stats - Raw statistics from backend.
 * @returns {Object} Derived statistics including counts and distributions.
 */
export const useCustomerStats = (stats = {}) => {
  return useMemo(() => {
    const statusDistribution = stats.statusDistribution || [];
    const referenceDistribution = stats.referenceDistribution || [];
    const ContactsCount = stats.active ?? 0;
    const teleconvertedCount = stats.converted ?? 0;
    const telereferralsCount = referenceDistribution
      .filter(s => !['manual', 'bulk import', 'direct', 'website', 'csv import'].includes(String(s.name || "").toLowerCase().trim()))
      .reduce((acc, curr) => acc + curr.value, 0);
    const teleunassignedCount = stats.unassigned ?? 0;
    const teleconversionRate = stats.teleconversionRate ?? (
      ContactsCount > 0 ? Math.round((teleconvertedCount / ContactsCount) * 100) : 0
    );
    console.log("Derived Stats:", (stats));

    const telestatusBreakdown = statusDistribution;
    const telereferralBreakdown = referenceDistribution.filter(s =>
      !['manual', 'bulk import', 'direct', 'website', 'csv import'].includes(String(s.name || "").toLowerCase().trim())
    );

    const telediscussionCount = statusDistribution
      .filter(s => ['not_reachable', 'not_answered', 'contact_later', 'interested'].includes(String(s.name || "").toLowerCase().trim()))
      .reduce((acc, curr) => acc + curr.value, 0);

    return {
      ContactsCount,
      teleconvertedCount,
      telereferralsCount,
      teleunassignedCount,
      teleconversionRate,
      telestatusBreakdown,
      telereferralBreakdown,
      telediscussionCount
    };
  }, [stats]);
};

/**
 * Hook to derive customer statistics directly from the filtered leads array.
 * This guarantees 100% synchronization with the table.
 */
export const useDerivedCustomerStats = (leads = []) => {
  return useMemo(() => {
    const activeLeads = leads.filter(l => !l.isDisabled);
    const ContactsCount = activeLeads.length;
    
    const teleconvertedCount = activeLeads.filter(l => {
      const status = l.status === "drop" ? "drop" : (l.teleStatus || l.status);
      return status === 'converted' || l.isConvertedByTelecaller;
    }).length;

    const teleinterestedCount = activeLeads.filter(l => {
      const status = String(l.status === "drop" ? "drop" : (l.teleStatus || l.status || "")).toLowerCase();
      return status === 'interested';
    }).length;

    const teleappointedCount = activeLeads.filter(l => {
      const status = String(l.status === "drop" ? "drop" : (l.teleStatus || l.status || "")).toLowerCase();
      return status === 'appointment_scheduled';
    }).length;
    
    const telereferralsCount = activeLeads.filter(l => {
      const source = String(l.source || "").toLowerCase().trim();
      return !['manual', 'bulk import', 'direct', 'website', 'csv import', ''].includes(source);
    }).length;
    
    const teleunassignedCount = activeLeads.filter(l => !l.telecaller && !l.assignedTo).length;
    
    const telediscussionCount = activeLeads.filter(l => {
      const status = String(l.teleStatus || l.status || "").toLowerCase();
      return ['not_reachable', 'not_answered', 'contact_later', 'interested'].includes(status);
    }).length;

    const teleconversionRate = ContactsCount > 0 ? Math.round((teleconvertedCount / ContactsCount) * 100) : 0;
    
    const statusMap = activeLeads.reduce((acc, l) => {
      const status = l.status === "drop" ? "drop" : (l.teleStatus || l.status || "new");
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});
    const telestatusBreakdown = Object.entries(statusMap).map(([name, value]) => ({ name, value }));
    
    const referralMap = activeLeads.reduce((acc, l) => {
      const source = String(l.source || "").trim();
      const sourceLower = source.toLowerCase();
      if (!['manual', 'bulk import', 'direct', 'website', 'csv import', ''].includes(sourceLower)) {
        acc[source || "Unknown"] = (acc[source || "Unknown"] || 0) + 1;
      }
      return acc;
    }, {});
    const telereferralBreakdown = Object.entries(referralMap).map(([name, value]) => ({ name, value }));

    return {
      ContactsCount,
      teleconvertedCount,
      teleinterestedCount,
      teleappointedCount,
      telereferralsCount,
      teleunassignedCount,
      telediscussionCount,
      teleconversionRate,
      telestatusBreakdown,
      telereferralBreakdown
    };
  }, [leads]);
};

/**
 * Hook to derive and format lead statistics from raw backend stats.
 * @param {Object} stats - Raw statistics from backend leads query.
 * @returns {Object} Derived statistics for leads.
 */
export const useLeadStats = (stats = {}) => {
  return useMemo(() => {
    console.log("Raw Lead Stats in useLeadStats:", stats);

    const statusDistribution = stats.statusDistribution || [];
    const sourceDistribution = stats.sourceDistribution || [];

    const convertedCount = stats.converted ?? 0;
    const leadconvertedCount = stats.leadconverted ?? 0; // mapped from backend
    const newLeads = stats.newLeads || 0;
    const budget = stats.budget || 0;
    const rate = stats.rate || 0;

    const statusBreakdown = statusDistribution;
    const sourceBreakdown = sourceDistribution.filter(s =>
      !['manual', 'bulk import', 'direct', 'website', 'csv import'].includes(String(s.name || "").toLowerCase().trim())
    );

    const derivedStats = {
      convertedCount,
      leadconvertedCount,
      newLeads,
      budget,
      rate,
      statusBreakdown,
      sourceBreakdown
    };

    console.log("Derived Lead Stats:", derivedStats);

    return derivedStats;
  }, [stats]);
};

/**
 * Hook to derive project statistics directly from the filtered projects array.
 * This guarantees 100% synchronization with the table.
 */
export const useDerivedProjectStats = (projects = []) => {
  return useMemo(() => {
    const activeProjects = projects.filter(p => !p.isDisabled);
    const totalProjects = activeProjects.length;
    
    const IN_PROGRESS_STAGES = [
      'team assignment',
      'material requested',
      'pending store',
      'owner approval',
      'dispatched',
      'project started',
      'carpenter complete',
      'electrician complete',
      'painter complete',
      'plumber complete',
      'documents',
      'bill approval'
    ];

    const inProgress = activeProjects.filter(p => {
      const displayStatus = getProjectPipelineDisplayStatus(p).toLowerCase().trim();
      return IN_PROGRESS_STAGES.includes(displayStatus) || displayStatus.includes("progress");
    }).length;

    const completed = activeProjects.filter(p => {
      const displayStatus = getProjectPipelineDisplayStatus(p).toLowerCase().trim();
      return displayStatus === "completed" || displayStatus === "done" || displayStatus.includes("complete");
    }).length;

    const totalQuoted = activeProjects.reduce((sum, p) => {
      return sum + (Number(p.budget) || 0);
    }, 0);

    return {
      totalProjects,
      inProgress,
      completed,
      totalQuoted
    };
  }, [projects]);
};

/**
 * Hook to fetch raw team statistics from the backend.
 */
export const useTeamGlobalStats = () => {
  return useQuery({
    queryKey: ["team-global-stats"],
    queryFn: () => userService.getStats(),
    staleTime: 60000,
  });
};

/**
 * Hook to derive and format team statistics from raw backend stats.
 * @param {Object} stats - Raw statistics from backend employees query.
 * @returns {Object} Derived statistics for team members.
 */
export const useTeamStats = (stats = {}) => {
  return useMemo(() => {
    console.log("Raw Team Stats in useTeamStats:", stats);

    const totalMembers = stats.total || 0;
    const activeMembers = stats.activeCount || 0;
    const inactiveMembers = stats.inactiveCount || 0;
    const locationsCount = stats.locations || 0;
    const roleBreakdown = stats.roleBreakdown || [];
    const locationBreakdown = stats.locationBreakdown || [];

    const derivedStats = {
      totalMembers,
      activeMembers,
      inactiveMembers,
      locationsCount,
      roleBreakdown,
      locationBreakdown
    };

    console.log("Derived Team Stats:", derivedStats);

    return derivedStats;
  }, [stats]);
};

/**
 * Hook to derive and format expense statistics from a list of expenses.
 * @param {Array} expenses - List of expense records.
 * @returns {Object} Formatted expense statistics.
 */
export const useExpenseStats = (expenses = []) => {
  return useMemo(() => {
    const getFinalAmount = (p) => Math.abs(p.approvedAmount || p.amount || 0);

    const requestedTotal = expenses.reduce((s, p) => s + Math.abs(p.amount || 0), 0);
    const approvedTotal = expenses
      .filter(p => ['approved', 'paid', 'owner_approved'].includes(p.status))
      .reduce((s, p) => s + getFinalAmount(p), 0);

    const projectTotal = expenses
      .filter(p => p.relatedToProject === true || String(p.relatedToProject).toLowerCase() === 'yes')
      .reduce((s, p) => s + getFinalAmount(p), 0);

    const generalTotal = expenses
      .filter(p => !(p.relatedToProject === true || String(p.relatedToProject).toLowerCase() === 'yes'))
      .reduce((s, p) => s + getFinalAmount(p), 0);

    // Monthly trend calculation
    const monthlyDataObj = expenses.reduce((acc, exp) => {
      const d = new Date(exp.date || exp.createdAt);
      if (isNaN(d.getTime())) return acc;
      const month = d.toLocaleString('default', { month: 'short' });
      acc[month] = (acc[month] || 0) + getFinalAmount(exp);
      return acc;
    }, {});

    return {
      count: expenses.length,
      requestedTotal,
      approvedTotal,
      generalTotal,
      projectTotal,
      trendData: Object.entries(monthlyDataObj)
        .map(([name, value]) => ({ name, value }))
        .slice(-6)
    };
  }, [expenses]);
};

/**
 * Hook to derive and format oversight statistics (Inflow vs Outflow).
 * @param {Object} stats - Raw statistics from oversight data processing.
 * @returns {Object} Formatted oversight stats.
 */
export const useOversightStats = (stats = {}) => {
  return useMemo(() => {
    const {
      totalPayments = 0,
      totalProjectExpenses = 0,
      pendingGeneralExpenses = 0,
      balance = 0
    } = stats;

    return {
      totalCollected: totalPayments,
      totalOutflow: totalProjectExpenses,
      disbursementReady: pendingGeneralExpenses,
      remainingBalance: balance,
      isNegative: balance < 0
    };
  }, [stats]);
};

/**
 * Hook to derive and format account statistics (Invoicing, Collections, etc.).
 * @param {Object} stats - Raw statistics from accounts data processing.
 * @returns {Object} Formatted account stats.
 */
export const useAccountsStats = (stats = {}) => {
  return useMemo(() => {
    const {
      totalInvoiced = 0,
      totalReceived = 0,
      totalPending = 0,
      fullyPaidCount = 0,
      totalProjects = 0
    } = stats;

    return {
      billedAmount: totalInvoiced,
      receivedAmount: totalReceived,
      pendingCollection: totalPending,
      completionRate: totalProjects > 0 ? Math.round((fullyPaidCount / totalProjects) * 100) : 0,
      activeProjects: totalProjects,
      fullyPaidCount,
      totalExpensesCount: stats.totalExpensesCount || 0,
      paidExpensesCount: stats.paidExpensesCount || 0
    };
  }, [stats]);
};

/**
 * Hook to derive and format inventory statistics.
 * @param {Object} datasets - Contains inventory, sales, returns, etc.
 * @returns {Object} Formatted inventory stats for all sub-modules.
 */
export const useInventoryStats = ({
  inventory = [],
  sales = [],
  returns = [],
  requests = [],
  purchaseReturns = [],
  purchases = [],
  openingStocks = [],
  activeTab = 'solar'
} = {}) => {
  return useMemo(() => {
    // Ensure arrays are valid
    const safeInventory = Array.isArray(inventory) ? inventory : [];
    const safeSales = Array.isArray(sales) ? sales : [];
    const safeReturns = Array.isArray(returns) ? returns : [];
    const safeRequests = Array.isArray(requests) ? requests : [];
    const safePurchaseReturns = Array.isArray(purchaseReturns) ? purchaseReturns : [];

    // 1. Core Stock Metrics
    const lowStock = safeInventory.filter(i => (i.availableQty || 0) <= (i.minQty || 0) && (i.availableQty || 0) > 0 && (i.minQty || 0) > 0);
    const noStock = safeInventory.filter(i => (i.availableQty || 0) <= 0);
    // Use totalCost from master aggregation (sum of purchase netBills); fall back to qty * unitPrice
    const totalValue = safeInventory.reduce((sum, i) => sum + (Number(i.totalCost) || ((i.availableQty || 0) * (i.unitPrice || 0))), 0);
    const totalUnits = safeInventory.reduce((sum, i) => sum + (i.availableQty || 0), 0);

    // 2. Sales & Revenue
    const totalRevenue = safeSales.reduce((sum, s) => sum + (s.netPayable || 0), 0);
    const totalSalesQty = safeSales.reduce((sum, s) => {
      const qty = s.items?.reduce((is, it) => is + (it.qty || 0), 0) || 0;
      return sum + qty;
    }, 0);

    // 3. Material Requests & Pipeline
    const pendingRequests = safeRequests.filter(r => ['pending', 'awaiting_approval', 'owner_approval'].includes(r.status)).length;
    const movingRequests = safeRequests.filter(r => ['approved', 'dispatched', 'materials_dispatched'].includes(r.status)).length;
    const completedRequests = safeRequests.filter(r => ['delivered', 'fulfilled', 'completed'].includes(r.status)).length;

    // 4. Returns & Financial Recovery
    const totalReturnsValue = safeReturns.reduce((sum, r) => sum + (r.totalValue || r.amount || 0), 0);
    const prTotalValue = safePurchaseReturns.reduce((sum, r) => sum + (r.amount || 0), 0);

    // 5. Distribution by Category (Top 5)
    const categoryMap = safeInventory.reduce((acc, it) => {
      const cat = typeof it.category === 'object' ? (it.category?.title || it.category?.name) : it.category || 'Uncategorized';
      acc[cat] = (acc[cat] || 0) + (it.availableQty || 0);
      return acc;
    }, {});

    const distribution = Object.entries(categoryMap)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);

    const safePurchases = Array.isArray(purchases) ? purchases : [];
    const totalSpend = safePurchases.reduce((sum, p) => sum + (Number(p.netPayment || p.finalNetPayment || p.totalAmount || p.netBill || 0)), 0);
    const purchasesTotalQty = safePurchases.reduce((sum, p) => {
       return sum + (p.items || []).reduce((itemSum, it) => itemSum + (Number(it.quantity || it.qty || 0)), 0);
    }, 0);
    const purchasesTransactions = safePurchases.length;
    const purchasesSuppliersCount = new Set(safePurchases.map(p => {
      if (p.vendorName) return p.vendorName;
      if (typeof p.vendor === 'object' && p.vendor) return p.vendor.vendorName || p.vendor.name || p.vendor._id;
      if (p.vendor) return p.vendor;
      if (typeof p.supplier === 'object' && p.supplier) return p.supplier.name || p.supplier._id;
      return p.supplier || '';
    }).filter(Boolean)).size;

    const safeOpenStock = Array.isArray(openingStocks) ? openingStocks : [];
    const openStockTotalAmount = safeOpenStock.reduce((sum, o) => sum + ((Number(o.availableQty || o.quantity) || 0) * (Number(o.unitPrice || o.rate) || 0)), 0);
    const openStockTotalUnits = safeOpenStock.reduce((sum, o) => sum + (Number(o.availableQty || o.quantity) || 0), 0);
    const openStockCategoriesCount = new Set(safeOpenStock.map(o => o.categoryDisplay || o.category || '').filter(Boolean)).size;

    return {
      stock: {
        totalItems: safeInventory.length,
        totalValue,
        totalUnits,
        lowStockCount: lowStock.length,
        noStockCount: noStock.length,
        distribution
      },
      sales: {
        totalRevenue,
        totalQty: totalSalesQty,
        count: safeSales.length,
        transactions: safeSales.length,
        customersCount: new Set(safeSales.map(s => s.customer || '').filter(Boolean)).size
      },
      requests: {
        total: safeRequests.length,
        pending: pendingRequests,
        moving: movingRequests,
        completed: completedRequests
      },
      returns: {
        total: safeReturns.length + safePurchaseReturns.length,
        totalValue: totalReturnsValue + prTotalValue,
        siteReturns: safeReturns.length,
        purchaseReturns: safePurchaseReturns.length
      },
      purchases: {
        totalSpend,
        totalQty: purchasesTotalQty,
        transactions: purchasesTransactions,
        suppliersCount: purchasesSuppliersCount
      },
      openStock: {
        totalAmount: openStockTotalAmount,
        totalUnits: openStockTotalUnits,
        entriesCount: safeOpenStock.length,
        categoriesCount: openStockCategoriesCount
      }
    };
  }, [inventory, sales, returns, requests, purchaseReturns, purchases, openingStocks, activeTab]);
};





